/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useVoiceState.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing voice-related state (Azure voice selection, styles, audio config).
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useRef, useCallback } from 'react';
import type { AzureTTSVoice, VoiceStyles } from '../types';
import type { AiProfile } from '../api/aiProfilesApi';
import type { AiProvider } from '../types';

type FormData = Partial<AiProfile> & Pick<AiProfile, 'domain_id' | 'type' | 'model' | 'status'>;

export interface AzureAudioConfig {
  audioFormat: string;
  pitch: string;
  volume: string;
  rate: string;
}

export function useVoiceState(
  formData: FormData,
  selectedProvider: AiProvider | null,
  availableVoicesData: AzureTTSVoice[],
  handleInputChange: (field: string, value: unknown) => void
) {
  const [selectedVoiceData, setSelectedVoiceData] = useState<AzureTTSVoice | null>(null);
  const [selectedVoiceStyles, setSelectedVoiceStyles] = useState<string[]>([]);
  const [voiceSearch, setVoiceSearch] = useState('');
  const [isVoiceDropdownOpen, setIsVoiceDropdownOpen] = useState(false);
  const voiceDropdownRef = useRef<HTMLDivElement>(null);
  const [azureAudioConfig, setAzureAudioConfig] = useState<AzureAudioConfig>({
    audioFormat: 'riff-8khz-16bit-mono-pcm',
    pitch: 'medium',
    volume: 'medium',
    rate: 'medium'
  });
  const [voiceStyles, setVoiceStyles] = useState<VoiceStyles>({});

  // Track if we've initialized from voice_config to avoid loops
  const initializedFromConfigRef = useRef<number | string | null>(null);

  // Initialize from voice_config when loading an existing profile
  useEffect(() => {
    if (formData.id && formData.id !== initializedFromConfigRef.current && formData.voice_config && selectedProvider?.provider === 'azure') {
      try {
        const config = typeof formData.voice_config === 'string'
          ? JSON.parse(formData.voice_config)
          : formData.voice_config;

        if (config.audioConfig || config.rate || config.pitch || config.volume) {
          setAzureAudioConfig({
            audioFormat: config.audioConfig?.audioFormat || 'riff-8khz-16bit-mono-pcm',
            pitch: config.pitch || config.audioConfig?.pitch || 'medium',
            volume: config.volume || config.audioConfig?.volume || 'medium',
            rate: config.rate || config.audioConfig?.rate || 'medium'
          });
        }

        if (config.styles) {
          setVoiceStyles(config.styles);
        } else if (config.voicePersonality) {
          setVoiceStyles({ personality: config.voicePersonality });
        }

        initializedFromConfigRef.current = formData.id;
      } catch {
        // Silently fail if not valid JSON
      }
    } else if (!formData.id) {
      // For new profiles, allow re-initialization if ID becomes null
      initializedFromConfigRef.current = null;
    }
  }, [formData.id, formData.voice_config, selectedProvider?.provider]);

  // Update selectedVoiceData when voice_model (or model for Azure) or availableVoicesData changes
  useEffect(() => {
    const voiceToFind = formData.voice_model || (selectedProvider?.provider === 'azure' ? formData.model : '');

    if (voiceToFind && selectedProvider?.provider === 'azure') {
      const voice = availableVoicesData.find(v => v.ShortName === voiceToFind);
      if (voice) {
        setSelectedVoiceData(voice);
        const language = voice.DisplayLanguage || voice.Locale;
        const gender = voice.Gender;
        const name = voice.ShortName;
        setVoiceSearch(`${language} - ${gender} - ${name}`);

        // Initialize styles object with empty values if not already set and not currently loading from config
        setVoiceStyles(prev => {
          if (Object.keys(prev).length === 0 && voice.StyleList && voice.StyleList.length > 0) {
            const stylesObj: Record<string, string> = {};
            voice.StyleList.forEach(style => {
              stylesObj[style] = '';
            });
            return stylesObj;
          }
          return prev;
        });
      } else {
        setSelectedVoiceData(null);
      }
    } else {
      setSelectedVoiceData(null);
      if (selectedProvider?.provider !== 'azure') {
        setVoiceStyles({});
      }
    }
  }, [formData.voice_model, formData.model, availableVoicesData, selectedProvider?.provider]);

  const handleInputChangeRef = useRef(handleInputChange);
  useEffect(() => {
    handleInputChangeRef.current = handleInputChange;
  }, [handleInputChange]);

  // Sync voice config to formData whenever voiceStyles or voice_model changes
  useEffect(() => {
    // GUARD: For existing profiles, don't sync until initialization is complete
    // This prevents default "medium" values from overwriting saved values
    if (formData.id && initializedFromConfigRef.current !== formData.id) {
      return;
    }

    const voiceConfig: Record<string, unknown> = {};

    // Parse existing voice_config to preserve values set by other components
    let existingConfig: Record<string, unknown> = {};
    try {
      if (formData.voice_config) {
        existingConfig = typeof formData.voice_config === 'string'
          ? JSON.parse(formData.voice_config)
          : formData.voice_config;
      }
    } catch {
      existingConfig = {};
    }

    if (formData.voice_model) {
      voiceConfig.model = formData.voice_model;
    }

    if (selectedVoiceData) {
      voiceConfig.name = selectedVoiceData.ShortName;
      voiceConfig.fullName = selectedVoiceData.Name;
      voiceConfig.locale = selectedVoiceData.Locale;
      voiceConfig.gender = selectedVoiceData.Gender;
      voiceConfig.sampleRateHertz = selectedVoiceData.SampleRateHertz;

      if (Object.keys(voiceStyles).length > 0) {
        voiceConfig.styles = voiceStyles;
      }

      if ((voiceStyles as any).rolePlay) {
        voiceConfig.rolePlay = (voiceStyles as any).rolePlay;
      }

      if ((voiceStyles as any).contentCategories) {
        voiceConfig.contentCategories = (voiceStyles as any).contentCategories;
      }

      if (voiceStyles.personality) {
        (voiceConfig as any).voicePersonality = voiceStyles.personality;
      }

      // Preserve existing audio settings from formData.voice_config, only use azureAudioConfig as fallback
      // This prevents overwriting values set by AzureVoiceCascadingSelection
      voiceConfig.rate = existingConfig.rate || azureAudioConfig.rate;
      voiceConfig.pitch = existingConfig.pitch || azureAudioConfig.pitch;
      voiceConfig.volume = existingConfig.volume || azureAudioConfig.volume;
    }

    if (Object.keys(voiceConfig).length > 0) {
      handleInputChangeRef.current('voice_config', JSON.stringify(voiceConfig));
    }
  }, [voiceStyles, formData.voice_model, selectedVoiceData, azureAudioConfig]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (voiceDropdownRef.current && !voiceDropdownRef.current.contains(event.target as Node)) {
        setIsVoiceDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleVoiceStyleChange = useCallback((style: string, value: string) => {
    setVoiceStyles(prev => ({ ...prev, [style]: value }));
  }, []);

  const handleVoiceStyleSet = useCallback((field: string, value: string) => {
    setVoiceStyles(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleAzureConfigChange = useCallback((field: string, value: string) => {
    setAzureAudioConfig(prev => ({ ...prev, [field]: value }));
  }, []);

  return {
    selectedVoiceData,
    setSelectedVoiceData,
    selectedVoiceStyles,
    setSelectedVoiceStyles,
    voiceSearch,
    setVoiceSearch,
    isVoiceDropdownOpen,
    setIsVoiceDropdownOpen,
    voiceDropdownRef,
    azureAudioConfig,
    setAzureAudioConfig,
    voiceStyles,
    setVoiceStyles,
    handleVoiceStyleChange,
    handleVoiceStyleSet,
    handleAzureConfigChange
  };
}

